// 🔧 content_cache.js
// ✅ Purpose: In-memory Content Engine cache
// ✅ Features:
// - Stores published content bundles for the lifetime of the application
// - Prevents repeated Firestore reads while navigating the application
// - Cache is keyed by the canonical Content Bundle Key
// - Phase 1 intentionally uses memory only (no localStorage)
// - Future-ready for manifest/version validation and cache invalidation

/**
 * ============================================================================
 * CONTENT CACHE PHILOSOPHY
 * ============================================================================
 *
 * Phase 1 intentionally uses ONLY an in-memory cache.
 *
 * Benefits:
 *
 * ✔ Extremely fast
 * ✔ No repeated Firestore reads
 * ✔ Automatically resets on browser refresh
 * ✔ Simple debugging
 * ✔ No stale localStorage issues
 *
 * Future phases may introduce:
 *
 * - manifest validation
 * - background refresh
 * - cache expiration
 * - offline persistence
 *
 * ============================================================================
 */

const contentCache = new Map();

/**
 * Returns the total number of cached bundles.
 */
export function getContentCacheSize() {
  return contentCache.size;
}

/**
 * Returns true if a bundle exists in cache.
 */
export function hasCachedBundle(bundleKey) {
  return contentCache.has(bundleKey);
}

/**
 * Returns a cached bundle.
 *
 * Returns:
 * null when not found.
 */
export function getCachedBundle(bundleKey) {
  if (!bundleKey) {
    return null;
  }

  return contentCache.get(bundleKey) ?? null;
}

/**
 * Stores a bundle in memory.
 *
 * Bundle should already represent published content.
 */
export function setCachedBundle(bundleKey, bundleData) {
  if (!bundleKey) {
    console.error(
      "[Content Engine] Cannot cache bundle without bundle key."
    );

    return;
  }

  contentCache.set(bundleKey, {
    cachedAt: Date.now(),

    bundle: bundleData,
  });

  console.log(
    "[Content Engine] Cached bundle:",
    bundleKey
  );
}

/**
 * Removes one bundle from cache.
 */
export function removeCachedBundle(bundleKey) {
  contentCache.delete(bundleKey);
}

/**
 * Clears the entire cache.
 *
 * Useful after:
 * - admin publish
 * - logout
 * - manual refresh
 * - future manifest mismatch
 */
export function clearContentCache() {
  contentCache.clear();

  console.log(
    "[Content Engine] Content cache cleared."
  );
}

/**
 * Returns cache diagnostics.
 *
 * Helpful during development.
 */
export function getContentCacheDiagnostics() {
  return {
    bundleCount: contentCache.size,

    bundleKeys: [...contentCache.keys()],
  };
}

/**
 * Returns every cached bundle.
 *
 * Primarily intended for debugging tools.
 */
export function getAllCachedBundles() {
  return [...contentCache.entries()];
}

/**
 * Returns the timestamp when a bundle
 * entered the cache.
 */
export function getBundleCachedTime(bundleKey) {
  const cached = contentCache.get(bundleKey);

  return cached?.cachedAt ?? null;
}

/**
 * Returns only the bundle payload.
 *
 * Most callers should use this helper.
 */
export function getBundleContent(bundleKey) {
  const cached = contentCache.get(bundleKey);

  return cached?.bundle ?? null;
}

/**
 * Replaces an existing bundle.
 *
 * This helper exists because future publish
 * operations will rebuild bundles after edits.
 */
export function replaceCachedBundle(
  bundleKey,
  bundleData
) {
  setCachedBundle(bundleKey, bundleData);
}

/**
 * Returns true when the cache contains
 * no bundles.
 */
export function isContentCacheEmpty() {
  return contentCache.size === 0;
}